import { DocumentDeposits } from './DocumentDeposits';
import { DocumentFilePicto } from './DocumentFilePicto';
import { formatSize } from '../lib/document-format';
import { useMemo, useState } from 'react';
import {
  ExternalLinkIcon,
  FileSearchIcon,
  CloudIcon,
  Loader2Icon,
  MessageSquareTextIcon,
  MoreVerticalIcon,
  RotateCcwIcon,
  Trash2Icon,
  UploadCloudIcon,
} from 'lucide-react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { DocumentDTO } from '@shared/schemas/document';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { getChatErrorMessage } from '@/features/chat/chat-errors';
import {
  buildNewChatLocationState,
  buildNewChatPath,
  buildRagScopedChatNavigation,
} from '@/features/chat/chat-session-intent';
import { cn } from '@/lib/utils';
import {
  documentQueryKey,
  deleteIndexedDocument,
  getDocumentContentUrl,
  listIndexedDocuments,
  reindexIndexedDocument,
} from '../services/documents.api';
import {
  buildDocumentsListSearchParams,
  parseDocumentsListParams,
  type DocumentsListParams,
  type DocumentsOriginFilter,
} from '../lib/documents-list-params';


const DOCUMENTS_PAGE_SIZE = 24;

function formatDate(value: string) {
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function documentMetaLine(document: DocumentDTO) {
  const parts: string[] = [formatSize(document.sizeBytes), formatDate(document.updatedAt)];

  if (typeof document.pageCount === 'number') {
    parts.unshift(`${document.pageCount} p.`);
  }

  return parts.join(' · ');
}

function documentUploaderLine(document: DocumentDTO) {
  if (document.uploadedByName) {
    return `Ajouté par ${document.uploadedByName}`;
  }

  if (document.uploadedByEmail) {
    return `Ajouté par ${document.uploadedByEmail}`;
  }

  return null;
}

function statusLabel(document: DocumentDTO) {
  switch (document.status) {
    case 'pending':
      return 'En attente';
    case 'uploading':
      return 'Envoi';
    case 'indexing':
      return 'Indexation';
    case 'completed':
      return 'Indexé';
    case 'failed':
      return 'Échec';
    default:
      return document.status;
  }
}

function DocumentStatusBadge({ document }: { document: DocumentDTO }) {
  const label = statusLabel(document);
  const isBusy = document.status === 'pending' || document.status === 'uploading' || document.status === 'indexing';

  const variantClass = (() => {
    switch (document.status) {
      case 'completed':
        return 'border-emerald-500/25 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100';
      case 'failed':
        return '';
      case 'pending':
        return 'text-muted-foreground';
      case 'indexing':
      case 'uploading':
        return 'border-amber-500/25 bg-amber-500/10 text-amber-950 dark:text-amber-50';
      default:
        return 'text-muted-foreground';
    }
  })();

  return (
    <Badge
      variant={document.status === 'failed' ? 'destructive' : 'outline'}
      data-testid={`document-status-${document.id}`}
      className={cn(
        'shrink-0 gap-1 font-medium',
        document.status === 'completed' && variantClass,
        (document.status === 'uploading' || document.status === 'indexing') && variantClass,
        document.status === 'pending' && variantClass,
      )}
    >
      {isBusy ? (
        <Loader2Icon className="size-3 animate-spin" aria-hidden />
      ) : null}
      {label}
    </Badge>
  );
}

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { origin, page, search } = parseDocumentsListParams(searchParams);

  // Filters replace the history entry (typing must not stack entries) and keep the
  // navigation state, so the back button still knows where the user came from.
  function updateListParams(patch: Partial<DocumentsListParams>) {
    setSearchParams((current) => buildDocumentsListSearchParams({ ...parseDocumentsListParams(current), ...patch }), {
      replace: true,
      state: location.state,
    });
  }

  function setPage(nextPage: number) {
    updateListParams({ page: nextPage });
  }
  // The candidate outlives the dialog so its name stays visible during the closing animation.
  const [deletionCandidate, setDeletionCandidate] = useState<DocumentDTO | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const documentsQuery = useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => listIndexedDocuments({
      page,
      pageSize: DOCUMENTS_PAGE_SIZE,
      origin: origin === 'all' ? undefined : origin,
      search: search.trim() || undefined,
    }),
    queryKey: [...documentQueryKey, search, origin, page],
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const hasPendingIndexation = items.some((document) => (
        document.status === 'pending' || document.status === 'indexing' || document.status === 'uploading'
      ));

      return hasPendingIndexation ? 4_000 : false;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => deleteIndexedDocument(documentId),
    onError: (error) => {
      toast.error(getChatErrorMessage(error));
    },
    onSuccess: async () => {
      toast.success('Document supprimé.');
      await queryClient.invalidateQueries({ queryKey: documentQueryKey });
    },
  });

  const reindexMutation = useMutation({
    mutationFn: (documentId: string) => reindexIndexedDocument(documentId),
    onError: (error) => {
      toast.error(getChatErrorMessage(error));
    },
    onSuccess: async () => {
      toast.success('Indexation relancée.');
      await queryClient.invalidateQueries({ queryKey: documentQueryKey });
    },
  });

  const viewMutation = useMutation({
    mutationFn: async (documentId: string) => getDocumentContentUrl(documentId),
    onSuccess: (contentUrl) => {
      window.open(contentUrl, '_blank', 'noopener,noreferrer');
    },
  });

  const documents = useMemo(() => documentsQuery.data?.items ?? [], [documentsQuery.data?.items]);
  const pageCount = documentsQuery.data?.meta.pageCount ?? 1;
  const totalDocuments = documentsQuery.data?.meta.total ?? 0;
  // A deletion can empty the last page: show the last existing one.
  const currentPage = Math.min(page, pageCount);

  function confirmDeletion() {
    if (!deletionCandidate) {
      return;
    }
    deleteMutation.mutate(deletionCandidate.id, {
      onSuccess: () => {
        if (documents.length === 1 && currentPage > 1) {
          setPage(currentPage - 1);
        }
      },
    });
    setIsDeleteDialogOpen(false);
  }
  return (
    <section className="w-full space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Base documentaire</h1>
          <p className="text-sm text-muted-foreground">
            Les dépôts manuels sont partagés dans l’entreprise ; les documents SharePoint conservent leurs droits
            d’accès d’origine. Les formats Office sont convertis en PDF avant indexation.
          </p>
        </div>
        <Button asChild variant="outline" className="shrink-0 gap-2 rounded-full">
          <Link
            to={buildNewChatPath('document-search')}
            state={buildNewChatLocationState()}
            data-testid="documents-ask-base-button"
          >
            <FileSearchIcon className="size-4" aria-hidden />
            Interroger la base
          </Link>
        </Button>
      </div>

      <DocumentDeposits />

      <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-card p-5">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="text-lg font-semibold text-foreground">Documents indexés</h2>
          <div className="flex flex-col gap-2 sm:flex-row">
            <ToggleGroup
              aria-label="Filtrer par origine"
              type="single"
              value={origin}
              variant="outline"
              spacing={0}
              onValueChange={(value) => {
                if (!value) return;
                updateListParams({ origin: value as DocumentsOriginFilter, page: 1 });
              }}
            >
              <ToggleGroupItem value="all">Tous</ToggleGroupItem>
              <ToggleGroupItem value="manual">Dépôt manuel</ToggleGroupItem>
              <ToggleGroupItem value="m365_sharepoint">SharePoint</ToggleGroupItem>
            </ToggleGroup>
            <Input
              className="max-w-xs"
              placeholder="Rechercher un document"
              aria-label="Rechercher un document"
              value={search}
              onChange={(event) => {
                updateListParams({ page: 1, search: event.target.value });
              }}
            />
          </div>
        </div>

        {documentsQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" />
            Chargement des documents…
          </div>
        ) : documents.length === 0 && origin === 'm365_sharepoint' ? (
          <div className="relative overflow-hidden rounded-[var(--radius-surface-3xl)] border border-border/70 bg-muted/30 px-6 py-12">
            <div
              aria-hidden
              className="pointer-events-none absolute -top-16 left-1/2 size-56 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl"
            />
            <div className="relative flex flex-col items-center gap-4 text-center">
              <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-background text-primary ring-1 ring-border/70">
                <CloudIcon className="size-7" />
              </span>
              <div className="space-y-1.5">
                <div className="flex items-center justify-center gap-2">
                  <h3 className="text-base font-semibold text-foreground">
                    Connexion Microsoft&nbsp;365
                  </h3>
                  <Badge>Bientôt</Badge>
                </div>
                <p className="mx-auto max-w-md text-sm leading-6 text-muted-foreground">
                  Vos bibliothèques SharePoint se synchroniseront ici automatiquement, et
                  leurs documents deviendront interrogeables par l’assistant.
                </p>
              </div>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun document indexé pour le moment.</p>
        ) : (
          <ul className="grid list-none grid-cols-2 gap-3 p-0 sm:gap-4">
            {documents.map((document) => {
              const isOpening = viewMutation.isPending && viewMutation.variables === document.id;
              const isDeleting = deleteMutation.isPending && deleteMutation.variables === document.id;
              const isReindexing = reindexMutation.isPending && reindexMutation.variables === document.id;
              const menuBusy = isOpening || isDeleting;
              const canReindex = document.status === 'failed' && document.origin === 'manual' && document.hasSourceFile;

              return (
                <li key={document.id}>
                  <Card size="sm" className="h-full border-border/80 shadow-none ring-1 ring-border/60">
                    <CardHeader className="gap-0 pb-4">
                      <div className="flex items-start gap-2.5">
                        <DocumentFilePicto mimeType={document.mimeType} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-1.5">
                            <CardTitle className="line-clamp-2 min-w-0 flex-1 pt-0.5 text-sm font-semibold leading-snug">
                              {document.name}
                            </CardTitle>
                            <div className="flex shrink-0 items-center gap-1">
                              <Badge variant="secondary" className="gap-1">
                                {document.origin === 'm365_sharepoint'
                                  ? <CloudIcon className="size-3" />
                                  : <UploadCloudIcon className="size-3" />}
                                {document.origin === 'm365_sharepoint' ? 'SharePoint' : 'Dépôt manuel'}
                              </Badge>
                              <DocumentStatusBadge document={document} />
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="size-8 text-muted-foreground hover:text-foreground"
                                    disabled={menuBusy}
                                    aria-label={`Actions pour ${document.name}`}
                                  >
                                    {menuBusy ? (
                                      <Loader2Icon className="size-4 animate-spin" />
                                    ) : (
                                      <MoreVerticalIcon className="size-4" />
                                    )}
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-44">
                                  <DropdownMenuItem
                                    disabled={!document.hasSourceFile || isOpening}
                                    onClick={() => void viewMutation.mutate(document.id)}
                                  >
                                    {isOpening ? (
                                      <Loader2Icon className="size-4 animate-spin" />
                                    ) : (
                                      <ExternalLinkIcon className="size-4" />
                                    )}
                                    Consulter
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    variant="destructive"
                                    disabled={isDeleting}
                                    onClick={() => {
                                      setDeletionCandidate(document);
                                      setIsDeleteDialogOpen(true);
                                    }}
                                  >
                                    {isDeleting ? (
                                      <Loader2Icon className="size-4 animate-spin" />
                                    ) : (
                                      <Trash2Icon className="size-4" />
                                    )}
                                    Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
                            {documentMetaLine(document)}
                          </p>
                          {documentUploaderLine(document) ? (
                            <p className="text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
                              {documentUploaderLine(document)}
                            </p>
                          ) : null}
                          {document.status === 'completed' ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="mt-2.5 h-7 gap-1.5 rounded-full px-2.5 text-xs"
                              aria-label={`Discuter avec ${document.name}`}
                              onClick={() => {
                                const target = buildRagScopedChatNavigation([{ id: document.id, name: document.name }]);
                                navigate(target.path, { state: target.state });
                              }}
                            >
                              <MessageSquareTextIcon className="size-3.5" aria-hidden />
                              Discuter
                            </Button>
                          ) : null}
                          {document.status === 'failed' ? (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <p
                                className="min-w-0 flex-1 text-xs leading-relaxed text-destructive"
                                data-testid={`document-failure-${document.id}`}
                              >
                                {document.statusMessage ?? 'L’indexation a échoué.'}
                              </p>
                              {canReindex ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-7 gap-1.5 px-2.5 text-xs"
                                  disabled={isReindexing}
                                  onClick={() => reindexMutation.mutate(document.id)}
                                >
                                  {isReindexing
                                    ? <Loader2Icon className="size-3.5 animate-spin" aria-hidden />
                                    : <RotateCcwIcon className="size-3.5" aria-hidden />}
                                  Réessayer
                                </Button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}

        {pageCount > 1 ? (
          <nav
            aria-label="Pagination des documents"
            className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-4"
          >
            <p className="text-xs text-muted-foreground">
              {totalDocuments} document{totalDocuments > 1 ? 's' : ''} · page {currentPage} sur {pageCount}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage <= 1 || documentsQuery.isFetching}
                onClick={() => setPage(currentPage - 1)}
              >
                Précédent
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage >= pageCount || documentsQuery.isFetching}
                onClick={() => setPage(currentPage + 1)}
              >
                Suivant
              </Button>
            </div>
          </nav>
        ) : null}
      </div>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent data-testid="document-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {deletionCandidate?.name} » sera retiré de la base documentaire : l’assistant ne pourra plus
              le citer. Cette action est définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDeletion}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
